import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Button, Card, CircularProgress, SxProps, Typography } from '@mui/material';
import { UpdateCalculationPayload, getBlueprintCalculations } from 'features/blueprints';
import { useUpdateBlueprint } from 'features/blueprints/hooks/useUpdateBlueprint';
import { CurrencyNumberInput, PrintField } from 'greener-ui-library';
import { Blueprint } from 'types';
import { convertFromCents, convertToCents } from 'utils/currencyUtils';
import { getCurrencyFromBlueprint } from './utils';

interface BlueprintCalculationDetailsFormProps {
  blueprintDetails?: Blueprint;
  hideTitle?: boolean;
  readOnly?: boolean;
  sx?: SxProps;
}

interface CalculationFormData {
  // Conventional
  conventionalDieselPricePerLiter: number;
  conventionalGensetRentalCostPerWeek: number;
  conventionalAdditionalCostsPerWeek: number;
  // Hybrid
  hybridGensetRentalCost: number;
  hybridBatteryRentalCost: number;
  hybridDieselPricePerLiter: number;
  hybridElectricityPricePerKwh: number;
  hybridAdditionalCostsPerWeek: number;
  co2CostPerTon?: number;
}

export const BlueprintCalculationDetailsForm = ({
  blueprintDetails,
  hideTitle = false,
  readOnly = false,
  sx,
}: BlueprintCalculationDetailsFormProps) => {
  const { t } = useTranslation();
  const currency = getCurrencyFromBlueprint(blueprintDetails);
  const { updateBlueprint, isLoading } = useUpdateBlueprint();
  const [isDirty, setIsDirty] = useState(false);

  const { conventional: conventionalCalc, hybrid: hybridCalc } = getBlueprintCalculations(blueprintDetails);

  const { control, handleSubmit, reset, setValue, formState } = useForm<CalculationFormData>({
    defaultValues: {
      // Conventional
      conventionalDieselPricePerLiter: convertFromCents(
        conventionalCalc?.costAssumptions.dieselPricePerLiterPriceCents || 0
      ),
      conventionalGensetRentalCostPerWeek: convertFromCents(
        conventionalCalc?.costAssumptions.gensetRentalCostCents || 0
      ),
      conventionalAdditionalCostsPerWeek: convertFromCents(conventionalCalc?.costAssumptions.additionalCostsCents || 0),
      // Hybrid
      hybridGensetRentalCost: convertFromCents(hybridCalc?.costAssumptions.gensetRentalCostCents || 0),
      hybridBatteryRentalCost: convertFromCents(hybridCalc?.costAssumptions.batteryRentalCostCents || 0),
      hybridDieselPricePerLiter: convertFromCents(hybridCalc?.costAssumptions.dieselPricePerLiterPriceCents || 0),
      hybridElectricityPricePerKwh: convertFromCents(hybridCalc?.costAssumptions.energyPricePerKwhPriceCents || 0),
      hybridAdditionalCostsPerWeek: convertFromCents(hybridCalc?.costAssumptions.additionalCostsCents || 0),
      co2CostPerTon:
        hybridCalc?.costAssumptions.co2CostPerTonCents != undefined
          ? convertFromCents(hybridCalc.costAssumptions.co2CostPerTonCents)
          : undefined,
    },
  });

  // Reset form when blueprintDetails changes
  useEffect(() => {
    if (blueprintDetails) {
      reset({
        conventionalDieselPricePerLiter: convertFromCents(
          conventionalCalc?.costAssumptions.dieselPricePerLiterPriceCents || 0
        ),
        conventionalGensetRentalCostPerWeek: convertFromCents(
          conventionalCalc?.costAssumptions.gensetRentalCostCents || 0
        ),
        conventionalAdditionalCostsPerWeek: convertFromCents(
          conventionalCalc?.costAssumptions.additionalCostsCents || 0
        ),
        hybridGensetRentalCost: convertFromCents(hybridCalc?.costAssumptions.gensetRentalCostCents || 0),
        hybridBatteryRentalCost: convertFromCents(hybridCalc?.costAssumptions.batteryRentalCostCents || 0),
        hybridDieselPricePerLiter: convertFromCents(hybridCalc?.costAssumptions.dieselPricePerLiterPriceCents || 0),
        hybridElectricityPricePerKwh: convertFromCents(hybridCalc?.costAssumptions.energyPricePerKwhPriceCents || 0),
        hybridAdditionalCostsPerWeek: convertFromCents(hybridCalc?.costAssumptions.additionalCostsCents || 0),
        co2CostPerTon:
          hybridCalc?.costAssumptions.co2CostPerTonCents != undefined
            ? convertFromCents(hybridCalc.costAssumptions.co2CostPerTonCents)
            : undefined,
      });
      setIsDirty(false);
    }
  }, [blueprintDetails, conventionalCalc, hybridCalc, reset]);

  // Track if form is dirty
  useEffect(() => {
    setIsDirty(formState.isDirty);
  }, [formState.isDirty, isLoading]);

  const onSubmit = async (data: CalculationFormData) => {
    if (!blueprintDetails || !conventionalCalc || !hybridCalc) {
      return;
    }

    // Build updated calculations array (must send entire array)
    const updatedCalculations: UpdateCalculationPayload[] = [
      // Hybrid (with batteries)
      {
        calculation_name: hybridCalc.calculationName,
        is_base_calculation: false,
        currency_code: hybridCalc.currencyCode,
        cost_assumptions: {
          diesel_price_per_liter_price_cents: convertToCents(data.hybridDieselPricePerLiter),
          energy_price_per_kwh_price_cents: convertToCents(data.hybridElectricityPricePerKwh),
          genset_rental_cost_cents: convertToCents(data.hybridGensetRentalCost),
          battery_rental_cost_cents: convertToCents(data.hybridBatteryRentalCost),
          additional_costs_cents: convertToCents(data.hybridAdditionalCostsPerWeek),
          co2_cost_per_ton_cents: data.co2CostPerTon != null ? convertToCents(data.co2CostPerTon) : null,
        },
        power_setup: {
          generator_count: hybridCalc.powerSetup.generatorCount || 0,
          generator_capacity_kva: hybridCalc.powerSetup.generatorCapacityKva ?? null,
          generator_type_id: hybridCalc.powerSetup.generatorTypeId,
          battery_count: hybridCalc.powerSetup.batteryCount,
          battery_size_kwh: hybridCalc.powerSetup.batterySizeKwh,
          battery_converter_power_kw: hybridCalc.powerSetup.batteryConverterPowerKw,
          battery_type_id: hybridCalc.powerSetup.batteryTypeId,
          mains_connection_max_amps: hybridCalc.powerSetup.mainsConnectionMaxAmps ?? null,
          mains_connection_max_kw: hybridCalc.powerSetup.mainsConnectionMaxKw ?? null,
          power_source: hybridCalc.powerSetup.powerSource,
        },
      },
      // Conventional (base calculation, no batteries)
      {
        calculation_name: conventionalCalc.calculationName,
        is_base_calculation: true,
        currency_code: conventionalCalc.currencyCode,
        cost_assumptions: {
          diesel_price_per_liter_price_cents: convertToCents(data.conventionalDieselPricePerLiter),
          energy_price_per_kwh_price_cents: 0,
          genset_rental_cost_cents: convertToCents(data.conventionalGensetRentalCostPerWeek),
          battery_rental_cost_cents: 0,
          additional_costs_cents: convertToCents(data.conventionalAdditionalCostsPerWeek),
          co2_cost_per_ton_cents: data.co2CostPerTon != null ? convertToCents(data.co2CostPerTon) : null,
        },
        power_setup: {
          generator_count: conventionalCalc.powerSetup.generatorCount || 0,
          generator_capacity_kva: conventionalCalc.powerSetup.generatorCapacityKva ?? null,
          generator_type_id: conventionalCalc.powerSetup.generatorTypeId,
          battery_count: conventionalCalc.powerSetup.batteryCount,
          battery_size_kwh: conventionalCalc.powerSetup.batterySizeKwh,
          battery_converter_power_kw: conventionalCalc.powerSetup.batteryConverterPowerKw,
          battery_type_id: conventionalCalc.powerSetup.batteryTypeId,
          mains_connection_max_amps: conventionalCalc.powerSetup.mainsConnectionMaxAmps ?? null,
          mains_connection_max_kw: conventionalCalc.powerSetup.mainsConnectionMaxKw ?? null,
          power_source: conventionalCalc.powerSetup.powerSource,
        },
      },
    ];

    try {
      await updateBlueprint({
        blueprintId: blueprintDetails.blueprintId,
        data: {
          calculations: updatedCalculations,
        },
      });

      setIsDirty(false);
    } catch (error) {
      console.error('Failed to update blueprint:', error);
    }
  };

  const handleCancel = () => {
    reset();
    setIsDirty(false);
  };

  return (
    <Card sx={{ padding: 4, borderRadius: '8px', ...sx }}>
      {!hideTitle && (
        <Typography variant="h3" mb={3}>
          {t('blueprints:details.calculationDetails.title')}
        </Typography>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="subtitle2" mb={2}>
          {t('blueprints:wizard.fields.priceAssumptions.title')}
        </Typography>

        <Box sx={{ mb: 4 }} gap={3} display="flex" flexDirection="column">
          <Controller
            name="conventionalDieselPricePerLiter"
            control={control}
            render={({ field }) => (
              <PrintField editable={!readOnly}>
                <CurrencyNumberInput
                  label={t('blueprints:wizard.fields.priceAssumptions.fuelPrice')}
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                    // Also update hybrid diesel price
                    setValue('hybridDieselPricePerLiter', value, { shouldDirty: true });
                  }}
                  currency={currency}
                  disabled={readOnly}
                  fullWidth
                />
              </PrintField>
            )}
          />

          <Controller
            name="hybridElectricityPricePerKwh"
            control={control}
            render={({ field }) => (
              <PrintField editable={!readOnly}>
                <CurrencyNumberInput
                  label={t('blueprints:wizard.fields.priceAssumptionsHybrid.electricityPricePerKwh')}
                  value={field.value}
                  onChange={field.onChange}
                  currency={currency}
                  disabled={readOnly}
                  fullWidth
                />
              </PrintField>
            )}
          />

          <Controller
            name="co2CostPerTon"
            control={control}
            render={({ field }) => (
              <PrintField editable={!readOnly}>
                <CurrencyNumberInput
                  label={t('blueprints:wizard.fields.priceAssumptionsHybrid.co2CostPerTon')}
                  value={field.value}
                  onChange={field.onChange}
                  currency={currency}
                  disabled={readOnly}
                  fullWidth
                />
              </PrintField>
            )}
          />
        </Box>

        <Typography variant="subtitle2" mb={2}>
          {t('blueprints:wizard.fields.priceAssumptionsConventional.title')}
        </Typography>

        <Box sx={{ mb: 4 }} gap={3} display="flex" flexDirection="column">
          <Controller
            name="conventionalGensetRentalCostPerWeek"
            control={control}
            render={({ field }) => (
              <PrintField editable={!readOnly}>
                <CurrencyNumberInput
                  label={t('blueprints:wizard.fields.priceAssumptionsConventional.gensetRentalCostPerWeek')}
                  value={field.value}
                  onChange={field.onChange}
                  currency={currency}
                  disabled={readOnly}
                  fullWidth
                />
              </PrintField>
            )}
          />

          <Controller
            name="conventionalAdditionalCostsPerWeek"
            control={control}
            render={({ field }) => (
              <PrintField editable={!readOnly}>
                <CurrencyNumberInput
                  label={t('blueprints:wizard.fields.priceAssumptionsConventional.additionalCostsPerWeek')}
                  value={field.value}
                  onChange={field.onChange}
                  currency={currency}
                  disabled={readOnly}
                  fullWidth
                />
              </PrintField>
            )}
          />
        </Box>

        <Typography variant="subtitle2" mb={2}>
          {t('blueprints:wizard.fields.priceAssumptionsHybrid.title')}
        </Typography>

        <Box gap={3} display="flex" flexDirection="column">
          <Controller
            name="hybridGensetRentalCost"
            control={control}
            render={({ field }) => (
              <PrintField editable={!readOnly}>
                <CurrencyNumberInput
                  label={t('blueprints:wizard.fields.suggestedRentals.gensets.rentalCost')}
                  value={field.value}
                  onChange={field.onChange}
                  currency={currency}
                  disabled={readOnly}
                  fullWidth
                />
              </PrintField>
            )}
          />

          <Controller
            name="hybridBatteryRentalCost"
            control={control}
            render={({ field }) => (
              <PrintField editable={!readOnly}>
                <CurrencyNumberInput
                  label={t('blueprints:wizard.fields.suggestedRentals.battery.rentalCost')}
                  value={field.value}
                  onChange={field.onChange}
                  currency={currency}
                  disabled={readOnly}
                  fullWidth
                />
              </PrintField>
            )}
          />

          <Controller
            name="hybridAdditionalCostsPerWeek"
            control={control}
            render={({ field }) => (
              <PrintField editable={!readOnly}>
                <CurrencyNumberInput
                  label={t('blueprints:wizard.fields.priceAssumptionsHybrid.additionalCostsPerWeek')}
                  value={field.value}
                  onChange={field.onChange}
                  currency={currency}
                  disabled={readOnly}
                  fullWidth
                />
              </PrintField>
            )}
          />
        </Box>

        {!readOnly && (
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
            <Button type="submit" variant="outlined" disabled={!isDirty || isLoading}>
              {isLoading ? <CircularProgress size={20} /> : t('blueprints:details.calculationDetails.updateButton')}
            </Button>

            <Button onClick={handleCancel} disabled={isLoading}>
              {t('components:form.cancel')}
            </Button>
          </Box>
        )}
      </form>
    </Card>
  );
};
